import { useCallback, useEffect, useRef, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { CardsService } from "../services/cardsService";
import type { CardTransaction } from "../types/card-movements.types";

export interface UseCardTransactionDetailArgs {
  cardId: string;
  transactionId: string | null;
  /**
   * Bumped when the open movement changed server-side (a socket push about it): read again without going back
   * to the spinner, so a pending purchase that settles updates under the reader's eyes.
   */
  refreshKey?: number;
}

export interface UseCardTransactionDetailResult {
  detail: CardTransaction | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetches a single card transaction detail whenever `transactionId` changes. A sequence ref guards against
 * out-of-order responses when the user opens several transactions quickly. Exposes `refetch` so the error
 * state can offer a retry. Mirrors the wallets movement-detail hook.
 */
export const useCardTransactionDetail = ({
  cardId,
  transactionId,
  refreshKey,
}: UseCardTransactionDetailArgs): UseCardTransactionDetailResult => {
  const [detail, setDetail] = useState<CardTransaction | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seqRef = useRef(0);
  // Whether a full read (the spinner one) is out, so a silent re-read never steals its sequence.
  const loadingRef = useRef(false);

  const fetchDetail = useCallback(async () => {
    if (!transactionId) {
      seqRef.current += 1;
      loadingRef.current = false;
      setDetail(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    const seq = ++seqRef.current;
    loadingRef.current = true;
    setIsLoading(true);
    setError(null);
    setDetail(null);

    try {
      const response = await CardsService.getCardTransactionDetail(cardId, transactionId);
      if (seq !== seqRef.current) return;
      setDetail(response.data);
    } catch (err: unknown) {
      if (seq !== seqRef.current) return;
      setDetail(null);
      setError(HttpApiService.getErrorMessage(err) || "error");
    } finally {
      if (seq === seqRef.current) {
        loadingRef.current = false;
        setIsLoading(false);
      }
    }
  }, [cardId, transactionId]);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  // The silent re-read: keeps what is on screen and only replaces it with a successful answer (which also clears
  // an error shown meanwhile). Skips the first run, which `fetchDetail` above already covers.
  const seenRefreshKey = useRef(refreshKey);
  useEffect(() => {
    if (refreshKey === seenRefreshKey.current) return;
    seenRefreshKey.current = refreshKey;
    if (!transactionId) return;
    // The drawer's own read is still out and was sent before the change: read again from scratch instead.
    // Taking its sequence silently would drop its answer and leave the spinner on for good.
    if (loadingRef.current) {
      void fetchDetail();
      return;
    }
    const seq = ++seqRef.current;
    CardsService.getCardTransactionDetail(cardId, transactionId)
      .then((response) => {
        if (seq !== seqRef.current) return;
        setDetail(response.data);
        setError(null);
      })
      .catch(() => {
        // Keep the version on screen; the next push or a reopen reads again.
      });
  }, [refreshKey, cardId, transactionId, fetchDetail]);

  return { detail, isLoading, error, refetch: () => void fetchDetail() };
};
